import { useEffect, useState } from 'react';
import { Alert, Checkbox, DatePicker, Form, Input, Modal, Space, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import {
  createAutoSync,
  syncSubmissionsToGoogleSheets,
  type SyncSheetsResult,
} from '@/api/submissions';
import type { SubmissionListParams } from '@/types/submissions';
import { AutoSyncList, autoSyncQueryKey } from './AutoSyncList';

const { Text, Link } = Typography;

// localStorage keys are namespaced by communityId so switching between
// communities doesn't bleed the previous sync target's URL/tab name.
// `all` is used when no communityId is set (cross-community admin view).
const lsUrlKey = (communityId?: number) =>
  `admin.submissions.sheetUrl:${communityId ?? 'all'}`;
const lsNameKey = (communityId?: number) =>
  `admin.submissions.sheetName:${communityId ?? 'all'}`;
const DEFAULT_SHEET_NAME = 'Bài làm học viên';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Current filter context — same shape used by the Excel export. */
  filterParams: SubmissionListParams;
  /**
   * Suggested tab name derived from the current filter (e.g. an assignment's
   * dueDate formatted as `yyyyMMdd`). When present, it takes precedence over
   * the localStorage cache — the suggestion reflects what the admin is
   * looking at right now, which is more relevant than their last sync target.
   */
  suggestedSheetName?: string | null;
  /**
   * Default sheet URL configured on the community (from hkm-web Settings →
   * Bài tập tab). Takes priority over the per-community localStorage cache —
   * the community-level value is the canonical destination, localStorage is
   * just a per-machine fallback for communities that haven't configured one.
   */
  communityAssignmentResultSheetUrl?: string | null;
  /**
   * Google Sheets file title (fetched server-side when the community admin
   * saved the URL in hkm-web). Shown as helper text under the URL input so
   * the admin can verify they're syncing to the right file.
   */
  communityAssignmentResultSheetName?: string | null;
}

/**
 * Modal hỏi Sheet URL + name rồi POST sang `/admin/submissions/sync-google-sheets`.
 *
 * <p>LocalStorage giữ URL/sheet-name gần nhất để admin không phải paste mỗi lần
 * — fits anti-friction guidance: 2nd sync trở đi chỉ cần click "Đồng bộ".
 * Server vẫn validate URL nên localStorage chỉ là UX cache.
 *
 * <p>{@code suggestedSheetName} override LS khi present — current filter
 * context usually wins. {@code autoCreate} mặc định bật để backend tự tạo tab
 * mới nếu chưa tồn tại; admin tắt nếu muốn fail-fast trên typo.
 */
export function GoogleSheetsSyncModal({
  open,
  onClose,
  filterParams,
  suggestedSheetName,
  communityAssignmentResultSheetUrl,
  communityAssignmentResultSheetName,
}: Props) {
  const [sheetUrl, setSheetUrl] = useState<string>('');
  const [sheetName, setSheetName] = useState<string>(DEFAULT_SHEET_NAME);
  const [autoCreate, setAutoCreate] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<SyncSheetsResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [autoSync, setAutoSync] = useState(false);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const communityId = filterParams.communityId;

  // Auto-fill priority on open:
  //   1. communityAssignmentResultSheetUrl (configured in hkm-web Settings)
  //   2. localStorage cache (per-community, last value the admin typed here)
  //   3. empty
  // suggestedSheetName (dueDate-derived) still wins for the tab-name field.
  // Reset lastResult/error để admin thấy fresh state.
  useEffect(() => {
    if (!open) return;
    const urlKey = lsUrlKey(filterParams.communityId);
    const nameKey = lsNameKey(filterParams.communityId);
    const trimmedCommunityUrl =
      communityAssignmentResultSheetUrl?.trim() || '';
    setSheetUrl(trimmedCommunityUrl || (localStorage.getItem(urlKey) ?? ''));
    const fromLs = localStorage.getItem(nameKey);
    setSheetName(suggestedSheetName ?? fromLs ?? DEFAULT_SHEET_NAME);
    setAutoCreate(true);
    setLastResult(null);
    setErrorMsg(null);
    setAutoSync(false);
    setEndDate(null);
    setEndDateError(null);
  }, [
    open,
    suggestedSheetName,
    filterParams.communityId,
    communityAssignmentResultSheetUrl,
  ]);

  const handleSubmit = async () => {
    const trimmedUrl = sheetUrl.trim();
    if (trimmedUrl === '') {
      setErrorMsg('Vui lòng nhập URL Google Sheets.');
      return;
    }
    if (autoSync && !endDate) {
      setEndDateError('Chọn ngày dừng tự động đồng bộ.');
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await syncSubmissionsToGoogleSheets(filterParams, {
        sheetUrl: trimmedUrl,
        sheetName: sheetName.trim() || undefined,
        autoCreate,
      });
      localStorage.setItem(lsUrlKey(filterParams.communityId), trimmedUrl);
      localStorage.setItem(
        lsNameKey(filterParams.communityId),
        sheetName.trim() || DEFAULT_SHEET_NAME,
      );
      setLastResult(result);
      const tabSuffix = result.tabs.length > 1
        ? ` vào ${result.tabs.length} tab`
        : ` vào tab "${result.tabs[0]?.name ?? '—'}"`;
      message.success(`Đã đồng bộ ${result.totalRowCount} dòng${tabSuffix}`);
      // Đồng bộ tay chạy được rồi mới lưu cấu hình tự động — link sai / chưa share
      // thì admin thấy lỗi ngay thay vì để tác vụ nền lỗi 5 lần rồi tự dừng.
      if (autoSync && endDate && communityId !== undefined) {
        await createAutoSync(filterParams, {
          sheetUrl: trimmedUrl,
          sheetName: sheetName.trim() || undefined,
          autoCreate,
          endDate: endDate.format('YYYY-MM-DD'),
        });
        setAutoSync(false);
        setEndDate(null);
        message.success(`Đã bật tự động đồng bộ 30 phút/lần (00h–21h) đến hết ${endDate.format('DD/MM/YYYY')}`);
        await queryClient.invalidateQueries({ queryKey: autoSyncQueryKey(communityId) });
      }
    } catch (err) {
      setErrorMsg(extractApiErrorMessage(err, 'Không đồng bộ được Google Sheets.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={lastResult ? 'Đồng bộ lại' : 'Đồng bộ'}
      cancelText="Đóng"
      confirmLoading={submitting}
      title="Đồng bộ Google Sheets"
      // Responsive: fills 95% of mobile viewport, caps at 560px on desktop.
      width="95%"
      style={{ maxWidth: 560 }}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item
          label="URL Google Sheets"
          help="Ví dụ: https://docs.google.com/spreadsheets/d/XXXXX/edit"
          required
        >
          <Input
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="Dán URL sheet vào đây"
            disabled={submitting}
          />
          {/* Helper text: file name fetched by hkm-api when this community's
              admin saved the URL. Reassures the operator they're hitting the
              right sheet before pressing Đồng bộ. */}
          {communityAssignmentResultSheetName &&
            sheetUrl.trim() === (communityAssignmentResultSheetUrl ?? '').trim() && (
              <Text type="success" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                ✓ Tên file: {communityAssignmentResultSheetName}
              </Text>
            )}
        </Form.Item>

        {/* Khi autoCreate bật, backend tự split theo dueDate → mỗi ngày 1 tab,
            không dùng tên tab admin nhập. Ẩn input để khỏi gây hiểu lầm. */}
        {!autoCreate ? (
          <Form.Item
            label="Tên tab trong sheet"
            help={
              suggestedSheetName
                ? `Gợi ý từ hạn nộp bài tập: ${suggestedSheetName}`
                : 'Bỏ trống dùng "Bài làm học viên".'
            }
          >
            <Input
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder={DEFAULT_SHEET_NAME}
              disabled={submitting}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          help={
            autoCreate
              ? 'Mỗi hạn nộp sẽ tạo 1 tab riêng (vd 20260520, 20260521…). Submission không có hạn nộp vào tab "khong-han-nop".'
              : 'Tắt nếu muốn fail-fast khi tab không tồn tại.'
          }
        >
          <Checkbox
            checked={autoCreate}
            onChange={(e) => setAutoCreate(e.target.checked)}
            disabled={submitting}
          >
            Tự động tạo tab theo hạn nộp
          </Checkbox>
        </Form.Item>

        <Form.Item
          help={
            communityId === undefined
              ? 'Chọn một cộng đồng ở bộ lọc để bật tự động đồng bộ.'
              : 'Lưu bộ lọc đang chọn. Từ 00h đến 21h, cứ 30 phút hệ thống tự ghi lại sheet nếu có bài mới hoặc bài được sửa.'
          }
        >
          <Checkbox
            checked={autoSync}
            onChange={(e) => {
              setAutoSync(e.target.checked);
              setEndDateError(null);
            }}
            disabled={submitting || communityId === undefined}
          >
            Tự động đồng bộ mỗi 30 phút
          </Checkbox>
        </Form.Item>

        {autoSync ? (
          <Form.Item
            label="Ngày dừng"
            htmlFor="auto-sync-end-date"
            required
            validateStatus={endDateError ? 'error' : undefined}
            help={endDateError ?? 'Hệ thống chạy tới hết ngày này rồi tự dừng.'}
          >
            <DatePicker
              id="auto-sync-end-date"
              value={endDate}
              onChange={(d) => {
                setEndDate(d);
                setEndDateError(null);
              }}
              format="DD/MM/YYYY"
              disabledDate={(d) => d.isBefore(dayjs(), 'day')}
              placeholder="Chọn ngày"
              disabled={submitting}
              style={{ width: '100%' }}
            />
          </Form.Item>
        ) : null}

        <Alert
          type="warning"
          showIcon
          message="Sync sẽ ghi đè dữ liệu hiện có trong tab này."
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sheet phải được share quyền <b>Editor</b> cho email service
              account (nếu bị 403, lỗi sẽ kèm email cụ thể để share). Muốn
              chấm điểm hay ghi chú tay thì dùng tab riêng, vì tab đồng bộ bị
              ghi lại mỗi lần chạy.
            </Text>
          }
          style={{ marginBottom: 16 }}
        />

        {errorMsg ? (
          <Alert type="error" showIcon message="Lỗi" description={errorMsg} />
        ) : null}

        {lastResult ? (
          <Alert
            type="success"
            showIcon
            message={
              lastResult.tabs.length > 1
                ? `Đã ghi ${lastResult.totalRowCount} dòng vào ${lastResult.tabs.length} tab`
                : `Đã ghi ${lastResult.totalRowCount} dòng vào tab "${lastResult.tabs[0]?.name ?? '—'}"`
            }
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {lastResult.tabs.length > 1 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {lastResult.tabs
                      .slice(0, 6)
                      .map((t) => `${t.name} (${t.rowCount})`)
                      .join(', ')}
                    {lastResult.tabs.length > 6
                      ? ` + ${lastResult.tabs.length - 6} tab khác`
                      : ''}
                  </Text>
                ) : null}
                <Link
                  href={lastResult.spreadsheetUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Mở sheet →
                </Link>
              </Space>
            }
          />
        ) : null}

        {communityId !== undefined ? <AutoSyncList communityId={communityId} /> : null}
      </Form>
    </Modal>
  );
}
